import React, {useEffect, useRef, useState, useMemo, useId} from 'react';
import {
  Button,
  Heading,
  SelectList,
  Horizontal,
  Vertical,
  ThemeName,
  Link,
  Theme,
  ButtonWithPopout,
} from '@taopix/taopix-design-system';
import {ListItemDataType} from '@taopix/taopix-design-system/dist/types/Components/SelectList/SelectList';
import {useTranslation} from 'react-i18next';
import {useTaopix} from '../../Context/TaopixContext';
import {useUiState} from '../../Context/UserInterface/UserInterfaceContext';
import {ButtonProps} from '@taopix/taopix-design-system/dist/types/Components/Button/Button';
import {PopOutProps} from '@taopix/taopix-design-system/dist/types/Components/PopOut/PopOut';
import {useSelector} from 'react-redux';
import {LayoutSelector, TotalPageCountSelector} from '../../../Selectors';
import {AIMode, Common, CurrencyAdapter, NumBool} from '@taopix/taopix-core';
import {getScrollParent} from '../../../Utils/Common';
import {getAddOrDeletePageNumber, onPageCountChanged} from '../../../Utils/Page';
import {UserInterfaceActions} from '../../Context/UserInterface/UserInterfaceActionTypes';
import {ProductLayout, ReducerState} from '@taopix/taopix-core/dist/types/types';
import useSettings from '../../../Hooks/useSettings';
import {EdgeBehaviours} from '@taopix/taopix-design-system/dist/types/Components/Portal/PortalEnums';
import {useUserFeedback} from '../../Context/UserFeedback/UserFeedbackContext';
import {UserFeedbackActions} from '../../Context/UserFeedback/UserFeedbackActionTypes';

type AddPagesButtonProps = {
  pageNumber: number;
  buttonProps?: ButtonProps;
  popoutAnchorId?: string;
  popoutProps?: Partial<PopOutProps>;
  disabled?: boolean;
  showLabel?: boolean;
  pageType?: 'page' | 'print';
  dropDownEdgeBehaviour?: EdgeBehaviours;
  showCancelButton?: boolean;
};

const AddPagesButton = ({
  pageNumber,
  buttonProps,
  popoutAnchorId,
  disabled,
  showLabel = true,
  pageType = 'page',
  dropDownEdgeBehaviour = 'flip',
  showCancelButton,
  ...props
}: AddPagesButtonProps) => {
  const [addPagesPopOutOpen, setAddPagesPopOutOpen] = useState(false);
  const [selectedPageCount, setSelectedPageCount] = useState({value: '0', index: 0});
  const buttonRef = useRef<HTMLButtonElement>(null);
  const id = useId();

  const {t} = useTranslation(['*', 'optionsandextras', 'autofill']);
  const app = useTaopix();
  const ui = useUiState();
  const settings = useSettings();
  const userFeedback = useUserFeedback();

  const productLayout = useSelector(LayoutSelector);
  const pageCount = useSelector(TotalPageCountSelector);

  const currency = useMemo(
    () => CurrencyAdapter(settings.state.currency),
    [settings.state.currency.code]
  );

  const isLastPage = (() => {
    if (NumBool.True === productLayout.spreads) {
      return pageNumber === productLayout.lastpage || pageNumber + 1 === productLayout.lastpage;
    }

    return pageNumber === productLayout.lastpage;
  })();

  // If the page count is odd numbered and the page insert count is even
  // then we need to find the difference to "correct" the page count.
  // Only if it's the last page.
  const diff = (() => {
    if (!isLastPage) return 0;

    const remaining = pageCount % productLayout.pageinsertcount;
    if (0 === remaining) return 0;

    return productLayout.pageinsertcount - remaining;
  })();

  const pageCountListItems = useMemo(() => {
    const pageList: ListItemDataType[] = [];
    const productQuantity = productLayout.pricing.quantity || 0;
    // If the current page count is not divisible by the page insert count,
    // then we need to adjust the pages that can be added after the last page.
    const lastPageRequiresCorrection = isLastPage && 0 !== diff;
    const pageInsertCount = productLayout.pageinsertcount;

    const step = (() => {
      // If not the last page, return a fixed number.
      if (!isLastPage && NumBool.True === productLayout.spreads) {
        return 2;
      } else if (!isLastPage && 0 === productLayout.spreads) {
        return 1;
      }

      // Use the page insert count on the last page.
      return productLayout.pageinsertcount;
    })();

    // Create a pricing engine object based on the parameters passed in.
    const pricingEngine = new (app.core.get('pricingEngine'))(
      currency,
      productQuantity,
      productLayout.pricing,
      AIMode.Off
    );

    const labelSingular =
      pageType === 'page' ? t('optionsandextras:str_LabelPage') : t('str_Label1Print');
    const labelPlural =
      pageType === 'page' ? t('optionsandextras:str_LabelPages') : t('str_LabelNPrints');

    // Use the pricing engine to calculate the project price.
    // We are only interested in the different between page counts, so we do not need to pass external assets or calendar events/sets.
    const basePrice = pricingEngine.calculateProjectPrice(pageCount, [], 0, 0);
    const hasPricedPaperComponent = pricingEngine.hasPricedPaperComponent(
      productLayout.minpages,
      productLayout.maxpages,
      step
    );

    // Calculate the number of options needed for the dropdown.
    const pagesToAddListLength = (() => {
      let current = pageCount;
      let count = 0;

      while (current < productLayout.maxpages) {
        let addition = step;

        if (isLastPage) {
          // If the current page count is not a multiple of
          // the page insert count, we add the diff to round up the current page count.
          const remaining = current % pageInsertCount;
          const diffToRound = pageInsertCount - remaining;
          addition = pageInsertCount !== diffToRound ? diffToRound : step;
        }

        current += addition;
        count++;
      }

      return count;
    })();

    // Build array with the actual values for the dropdown.
    const pagesToAddList = Array.from({length: pagesToAddListLength}, (pageNumber, index) => {
      // Initial is needed
      const initial = isLastPage && lastPageRequiresCorrection ? 0 : step;
      const value = initial + index * step;
      const valueWithDiff = value + diff;

      // If the value with diff goes over the max page count, reduce to the max possible page count.
      if (isLastPage && pageCount + valueWithDiff > productLayout.maxpages) {
        return productLayout.maxpages - pageCount;
      }

      return valueWithDiff;
    });

    // Loop over the array to get the formatted dropdown values and labels.
    pagesToAddList.forEach(pagesToAdd => {
      let inputValue = pagesToAdd;
      const newPageCount = pageCount + pagesToAdd;

      // Adjust the page count if it goes over the max pages.
      // Can happen if it's a spread with an odd number of pages.
      if (NumBool.True === productLayout.spreads && inputValue >= productLayout.maxpages) {
        inputValue--;
      }

      const inputValueStringValue = inputValue.toString();

      let optionLabel = (1 === pagesToAdd ? labelSingular : labelPlural).replace(
        '^0',
        inputValueStringValue
      );

      if (settings.state.price.showPrice && hasPricedPaperComponent) {
        // Use the pricing engine to calculate the project price.
        const newPrice = pricingEngine.calculateProjectPrice(newPageCount, [], 0, 0);
        const rawPriceDiff = newPrice - basePrice;
        const priceDiff = app.core
          .get('pricingEngine')
          .formatCurrencyNumber(rawPriceDiff, currency);

        const newPageCountPricing = `${rawPriceDiff > 0 ? '+' : ''}${priceDiff}`;
        // if pagenumber in label matches recommended pages to add to project add the string, same for max pages
        if (
          optionLabel.substring(0, optionLabel.indexOf(' ')) ===
          String(ui.state.autoFillOptions.recommendedPageCount - pageCount)
        ) {
          optionLabel = `${optionLabel} (${newPageCountPricing}) ${t('*:str_MessageRecommended')}`;
        } else if (
          optionLabel.substring(0, optionLabel.indexOf(' ')) ===
          String(ui.state.autoFillOptions.maxPageCount - pageCount)
        ) {
          optionLabel = `${optionLabel} (${newPageCountPricing}) ${t(
            '*:str_Message1PicturePerPage'
          )}`;
        } else {
          optionLabel = `${optionLabel} (${newPageCountPricing})`;
        }
      }

      pageList.push({
        label: optionLabel,
        value: inputValueStringValue,
      } as ListItemDataType);
    });

    if (0 === pageList.length) {
      pageList.push({
        label: labelPlural.replace('^0', '0'),
        value: '0',
      } as ListItemDataType);
    }

    return pageList;
  }, [
    productLayout.code,
    productLayout.pageinsertcount,
    pageCount,
    ui.state.flags.recommendAddingPictures,
    ui.state.autoFillOptions.recommendedPageCount,
    ui.state.autoFillOptions.maxPageCount,
  ]);

  useEffect(() => {
    setSelectedPageCount({value: pageCountListItems[0].value, index: 0});
  }, [pageCount, pageCountListItems]);

  useEffect(() => {
    const scrollElement = getScrollParent(buttonRef?.current);
    // Add a scroll listener to the scrollParent to close the popout when it is scrolled.
    if (addPagesPopOutOpen) {
      // Add the listener after a delay to avoid being triggered by the scrollIntoView method used when opening
      scrollElement?.addEventListener('scroll', closePopout);
    }
    return () => {
      // Remove the handler
      scrollElement?.removeEventListener('scroll', closePopout);
    };
  }, [addPagesPopOutOpen]);

  const alertMessage = useMemo(() => {
    switch (true) {
      case productLayout.maxpages === pageCount: {
        return t('str_WarningNotInsertPage');
      }
      case Common.isBackCover(pageNumber): {
        return t('str_WarningNotInsertPageAtEnd');
      }
      case NumBool.True === productLayout.spreads &&
        1 === productLayout.firstpagenumber &&
        Common.isFrontCover(pageNumber): {
        return t('str_WarningNotInsertPageAtStart');
      }
      default: {
        return '';
      }
    }
  }, [pageNumber, pageCount]);

  /**
   * Closes the popup.
   */
  const closePopout = () => {
    if (addPagesPopOutOpen) {
      // Remove the scroll handler from the scrollParent
      const scrollElement = getScrollParent(buttonRef?.current);
      scrollElement?.removeEventListener('scroll', closePopout);
      // Close the popout
      setAddPagesPopOutOpen(false);
    }
  };

  const addPagesButtonId = `addPagesButton-${pageNumber}${id.replaceAll(':', '')}`;
  const addPagesButtonClasses = buttonProps?.className;

  const popoutProps: Partial<PopOutProps> = {
    displayMode: ui.state.isSmallScreen ? 'modal' : 'positioned',
    theme: 'tpx-theme-workarea',
    themeTip: 'Work area',
    contentTheme:
      '' !== alertMessage && !ui.state.isSmallScreen ? ThemeName.Warning : ThemeName.Popouts,
    arrow: ui.state.isSmallScreen ? undefined : true,
    anchorId: popoutAnchorId ? popoutAnchorId : addPagesButtonId,
    className: 'flex flex-col p-md space-y-sm max-w-[300rem]',
    anchorOrigin: {vertical: Vertical.Top, horizontal: Horizontal.Centre},
    transformOrigin: {vertical: Vertical.Bottom, horizontal: Horizontal.Centre},
    popDirection: 'up',
    offset: {x: 0, y: -10},
    repositionOnResize: true,
    excludeIdFromClickOutside: [addPagesButtonId, 'pageCountDropDown-' + pageNumber],
    edgeBehaviour: 'move',
    ...props.popoutProps,
  };

  const buttonLabel = t(`${'page' === pageType ? '*' : 'retroprint'}:str_ButtonAddPages`);

  const addPages = (
    method: string,
    numberOfPages: number,
    showBusy: boolean,
    insertAtPage?: number
  ) => {
    if (showBusy) ui.dispatch(UserInterfaceActions.setIsBusy(true));
    setAddPagesPopOutOpen(false);
    app.core
      .get('stateManager')
      .call<{}, ReducerState & {spineResized: boolean}>('addpages', {
        currentPage: pageNumber,
        method: method,
        noPages: numberOfPages,
        pageNumber: insertAtPage,
      })
      .then(response => {
        onPageCountChanged(
          {spineResized: response.spineResized || false},
          ui,
          response?.project.productLayout as ProductLayout,
          pageCount
        );
        return response;
      })
      .catch(error => {
        let message = app.core.get('server').getErrorFromResponse(error);
        if ('' === message || !message) {
          message = error;
        }
        userFeedback.dispatch(
          UserFeedbackActions.setErrorUserFeedback({
            title: '',
            message,
            showHeader: true,
          })
        );
      })
      .finally(() => {
        if (showBusy) ui.dispatch(UserInterfaceActions.setIsBusy(false));
        // Close the page actions palette if it is open
        if (ui.state.palette.section === 'pageActionsPalette') {
          ui.dispatch(UserInterfaceActions.setToolPaletteSection(null));
        }
      });
  };

  return (
    <>
      <ButtonWithPopout
        open={addPagesPopOutOpen}
        closeOnInteractions={false}
        onOpen={() => {
          setAddPagesPopOutOpen(true);
        }}
        onClose={() => {
          setAddPagesPopOutOpen(false);
        }}
        buttonProps={{
          id: addPagesButtonId,
          ref: buttonRef,
          label: showLabel ? buttonLabel : undefined,
          ...buttonProps,
          className: addPagesButtonClasses,
          disabled: disabled,
          showToolTip: false,
        }}
        popoutProps={{
          id: 'addPagesPopout-' + pageNumber,
          ...popoutProps,
        }}
      >
        {'' !== alertMessage ? (
          <>{alertMessage}</>
        ) : (
          <>
            <Heading size={3} className={'text-center'} id={'addPagesHeading'}>
              {t(`${'page' === pageType ? '*' : 'retroprint'}:str_TitleAddBlankPages`)}
            </Heading>
            {ui.state.flags.recommendAddingPictures && (
              <>
                <p>{`${t('autofill:str_LabelCurrentTotal')}: ${pageCount}`}</p>
                <Theme name={ThemeName.Information} className={'flex flex-col p-sm space-y-sm'}>
                  <p>
                    {t('autofill:str_MessageRecommendAddPagesToMeetAverage')
                      .replace('^0', `${ui.state.autoFillOptions.recommendedPageCount - pageCount}`)
                      .replace(
                        '^1',
                        settings.state.price.showPrice
                          ? `(${ui.state.autoFillOptions.recommendedPageCountPrice})`
                          : ''
                      )}
                  </p>
                  <Button
                    label={t('autofill:str_ButtonAddPagesToMeetRecommended')
                      .replace('^0', `${ui.state.autoFillOptions.recommendedPageCount - pageCount}`)
                      .replace(
                        '^1',
                        settings.state.price.showPrice
                          ? `(${ui.state.autoFillOptions.recommendedPageCountPrice})`
                          : ''
                      )}
                    onClick={() => {
                      addPages(
                        isLastPage ? 'append' : 'insert',
                        ui.state.autoFillOptions.recommendedPageCount - pageCount,
                        false,
                        getAddOrDeletePageNumber(pageNumber, productLayout)
                      );
                    }}
                  />
                </Theme>
              </>
            )}
            {ui.state.flags.recommendAddingPictures ? (
              <Link
                className={'underline text-center'}
                onClick={() => {
                  ui.dispatch(
                    UserInterfaceActions.setFlags({
                      recommendAddingPictures: false,
                    })
                  );
                }}
              >
                {t('autofill:str_LabelChooseUpsellNumberOfPages')}
              </Link>
            ) : (
              <>
                <SelectList
                  id={'pageCountSelector-' + pageNumber}
                  dropdownId={'pageCountDropDown-' + pageNumber}
                  items={pageCountListItems}
                  labelledBy={'addPagesHeading'}
                  size={ui.state.isSmallScreen ? 'medium' : 'small'}
                  onChange={(event: any) => {
                    setSelectedPageCount(event.target);
                  }}
                  selectedIndex={selectedPageCount.index}
                  popoutParentTheme={'tpx-theme-workarea'}
                  popoutParentThemeTip={'Work area'}
                />
                <Button
                  label={buttonLabel}
                  buttonStyle={'primary'}
                  size={ui.state.isSmallScreen ? 'medium' : 'small'}
                  onClick={() => {
                    addPages(
                      isLastPage ? 'append' : 'insert',
                      Number(selectedPageCount.value),
                      false,
                      getAddOrDeletePageNumber(pageNumber, productLayout)
                    );
                  }}
                />
                {(ui.state.isSmallScreen || showCancelButton) && (
                  <Button
                    label={t('*:str_ButtonCancel')}
                    buttonStyle={'tertiary'}
                    onClick={() => setAddPagesPopOutOpen(false)}
                    size={ui.state.isSmallScreen ? 'medium' : 'small'}
                  />
                )}
              </>
            )}
          </>
        )}
      </ButtonWithPopout>
    </>
  );
};

export default AddPagesButton;
